import { useState } from 'react';
import { theme } from '../theme.js';
import { useRunHistory } from '../lib/queries.js';
import type { RunHistoryEntry } from '../lib/queries.js';
import { actorLabel } from '../lib/people.js';
import { Icon } from './Icon.js';

/**
 * Everything recorded on one inspection — who checked what, what changed,
 * when it was submitted. Collapsed by default; loads only when opened.
 */
export function RunHistory({ orgId, runId }: { orgId: string; runId: string }) {
  const [open, setOpen] = useState(false);
  const { data = [], isLoading } = useRunHistory(orgId, runId, open);

  return (
    <div style={{ borderTop: `1px solid ${theme.border}` }}>
      <button onClick={() => setOpen(!open)} style={{
        display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '14px 18px',
        background: theme.bg, border: 'none', cursor: 'pointer', fontFamily: theme.font,
        fontSize: 13, fontWeight: 600, color: theme.textDim,
      }}>
        <Icon name={open ? 'chevronD' : 'chevron'} size={14} />
        Activity on this check
      </button>
      {open && (
        <div style={{ padding: '0 18px 12px', background: theme.surface }}>
          {isLoading && <Muted>Loading…</Muted>}
          {!isLoading && data.length === 0 && <Muted>Nothing recorded yet.</Muted>}
          {data.map((e) => (
            <div key={e.id} style={{ padding: '9px 0', borderTop: `1px solid ${theme.border}` }}>
              <div style={{ display: 'flex', gap: 10, alignItems: 'baseline' }}>
                <div style={{ flex: 1, fontSize: 13, color: theme.text }}>{describe(e)}</div>
                <time dateTime={e.createdAt} title={new Date(e.createdAt).toLocaleString()}
                  style={{ fontSize: 11, color: theme.textMuted, whiteSpace: 'nowrap' }}>
                  {new Date(e.createdAt).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' })}
                </time>
              </div>
              {changes(e).map((c) => (
                <div key={c} style={{ fontSize: 12, color: theme.textDim, marginTop: 2 }}>{c}</div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function describe(e: RunHistoryEntry): string {
  const actor = e.actorName ? actorLabel(e.actorName, e.actorActive) : 'System';
  switch (e.action) {
    case 'run.create':        return `${actor} started this check`;
    case 'run.submit':        return `${actor} submitted this check`;
    case 'check_item.update': return `${actor} checked ${e.equipmentName ?? 'an item'}`;
    default:                  return `${actor}: ${e.action}`;
  }
}

const FIELD_LABEL: Record<string, string> = {
  status: 'Status', note: 'Note', externalRef: 'Ref', flagged: 'Flagged',
};

/** "Status: missing → ok" lines for the fields that changed. */
function changes(e: RunHistoryEntry): string[] {
  if (e.action !== 'check_item.update' || !e.oldValue || !e.newValue) return [];
  return Object.keys(FIELD_LABEL)
    .filter((k) => JSON.stringify(e.oldValue![k] ?? null) !== JSON.stringify(e.newValue![k] ?? null))
    .map((k) => `${FIELD_LABEL[k]}: ${fmt(e.oldValue![k])} → ${fmt(e.newValue![k])}`);
}

function fmt(v: unknown): string {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'boolean') return v ? 'yes' : 'no';
  const s = String(v);
  return s.length > 60 ? `${s.slice(0, 57)}…` : s;
}

function Muted({ children }: { children: React.ReactNode }) {
  return <div style={{ fontSize: 13, color: theme.textMuted, padding: '8px 0' }}>{children}</div>;
}
